import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { LogOut } from "lucide-react";

const STATUS_LABEL_FR = { pending: "En attente", scheduled: "Planifié", completed: "Effectué", cancelled: "Annulé" };
const STATUS_LABEL_EN = { pending: "Pending", scheduled: "Scheduled", completed: "Completed", cancelled: "Cancelled" };
const LEAD_STATUS_FR = { new: "Nouveau", qualified: "Qualifié", quoted: "Devis envoyé", confirmed: "Confirmé", traveling: "En voyage", completed: "Terminé" };
const LEAD_STATUS_EN = { new: "New", qualified: "Qualified", quoted: "Quoted", confirmed: "Confirmed", traveling: "Traveling", completed: "Completed" };

function Tab({ id, active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 text-xs tracking-[0.24em] uppercase transition-colors ${active ? "text-[#0A1128] border-b-2 border-[#D4AF37]" : "text-[#0A1128]/50 hover:text-[#0A1128]"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

export default function TeamDashboard() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("reservations");
  const [leads, setLeads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const L = lang === "fr" ? STATUS_LABEL_FR : STATUS_LABEL_EN;
  const LL = lang === "fr" ? LEAD_STATUS_FR : LEAD_STATUS_EN;

  const load = async () => {
    try {
      const [m, l, c, b] = await Promise.all([
        api.get("/team/me"),
        api.get("/team/leads"),
        api.get("/team/callbacks"),
        api.get("/team/bookings"),
      ]);
      setMe(m.data);
      setLeads(l.data);
      setCallbacks(c.data);
      setBookings(b.data);
    } catch {
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const logout = async () => {
    await api.post("/team/logout");
    navigate("/login", { replace: true });
  };

  const updateCallback = async (id, status) => {
    await api.patch(`/team/callbacks/${id}`, { status });
    toast.success(lang === "fr" ? "Statut mis à jour" : "Status updated");
    load();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#0A1128]/60 font-serif italic">…</div>;

  // Filter leads that represent château "reservations" in progress
  const reservations = leads.filter((l) => ["qualified", "quoted", "confirmed", "traveling"].includes(l.status));
  const pendingCallbacks = callbacks.filter((c) => c.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#0A1128]/10 bg-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="overline text-[#D4AF37]">{lang === "fr" ? "Espace équipe" : "Team space"}</div>
            <div className="font-serif text-2xl text-[#0A1128]" data-testid="team-me-name">{me?.name}</div>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm text-[#0A1128]/60">{me?.email}</span>
            <button onClick={logout} className="text-xs tracking-[0.24em] uppercase text-[#0A1128]/70 hover:text-[#0A1128] flex items-center gap-2" data-testid="team-logout">
              <LogOut size={14} /> {lang === "fr" ? "Déconnexion" : "Sign out"}
            </button>
          </div>
        </div>
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex gap-1">
          <Tab id="reservations" active={tab === "reservations"} onClick={() => setTab("reservations")} testid="team-tab-reservations">
            {lang === "fr" ? `Réservations (${reservations.length})` : `Reservations (${reservations.length})`}
          </Tab>
          <Tab id="callbacks" active={tab === "callbacks"} onClick={() => setTab("callbacks")} testid="team-tab-callbacks">
            {lang === "fr" ? `Rappels (${pendingCallbacks})` : `Callbacks (${pendingCallbacks})`}
          </Tab>
          <Tab id="calls" active={tab === "calls"} onClick={() => setTab("calls")} testid="team-tab-calls">
            {lang === "fr" ? `Appels visio (${bookings.length})` : `Video calls (${bookings.length})`}
          </Tab>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
        {tab === "reservations" && (
          <div className="space-y-4" data-testid="reservations-list">
            {reservations.length === 0 && (
              <div className="text-[#0A1128]/50 italic">{lang === "fr" ? "Aucune réservation en cours." : "No reservations in progress."}</div>
            )}
            {reservations.map((l) => (
              <div key={l.lead_id} className="bg-white border border-[#0A1128]/10 p-6 grid md:grid-cols-4 gap-4" data-testid={`reservation-${l.lead_id}`}>
                <div>
                  <div className="font-serif text-xl text-[#0A1128]">{l.name}</div>
                  <div className="text-xs text-[#0A1128]/60">{l.email}</div>
                  {l.phone && <div className="text-xs text-[#0A1128]/60">{l.phone}</div>}
                </div>
                <div className="text-sm">
                  <div className="overline text-[#D4AF37]">{lang === "fr" ? "Voyage" : "Trip"}</div>
                  <div className="mt-1 text-[#0A1128]">{l.travel_type} · {l.party_size} pax</div>
                  {l.dates && <div className="text-xs text-[#0A1128]/60">{l.dates}</div>}
                  {l.budget && <div className="text-xs text-[#0A1128]/60">{l.budget}</div>}
                </div>
                <div className="text-xs text-[#0A1128]/70 line-clamp-4">{l.message}</div>
                <div>
                  <div className="overline text-[#D4AF37]">{lang === "fr" ? "Statut" : "Status"}</div>
                  <div className="mt-2 inline-block px-3 py-1 bg-[#D4AF37]/15 text-[#0A1128] text-xs tracking-wide">{LL[l.status] || l.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "callbacks" && (
          <div className="space-y-4" data-testid="callbacks-list">
            {callbacks.length === 0 && (
              <div className="text-[#0A1128]/50 italic">{lang === "fr" ? "Aucun rappel demandé." : "No callbacks requested."}</div>
            )}
            {callbacks.map((c) => (
              <div key={c.callback_id} className="bg-white border border-[#0A1128]/10 p-6 grid md:grid-cols-5 gap-4 items-center" data-testid={`callback-${c.callback_id}`}>
                <div>
                  <div className="font-serif text-lg text-[#0A1128]">{c.name}</div>
                  <a href={`tel:${c.phone}`} className="text-xs text-[#D4AF37] hover:underline">{c.phone}</a>
                </div>
                <div className="text-sm">
                  <div className="overline text-[#D4AF37]">{lang === "fr" ? "Souhaité" : "Preferred"}</div>
                  <div className="mt-1 text-[#0A1128]">{c.preferred_date}</div>
                  <div className="text-xs text-[#0A1128]/60">{c.preferred_time}</div>
                </div>
                <div className="text-xs text-[#0A1128]/70 line-clamp-3 md:col-span-1">{c.notes || "—"}</div>
                <div>
                  <div className="overline text-[#D4AF37]">{lang === "fr" ? "Statut" : "Status"}</div>
                  <select
                    value={c.status}
                    onChange={(e) => updateCallback(c.callback_id, e.target.value)}
                    className="mt-2 border border-[#0A1128]/20 px-3 py-2 text-sm bg-white w-full"
                    data-testid={`callback-status-${c.callback_id}`}
                  >
                    {Object.keys(L).map((k) => <option key={k} value={k}>{L[k]}</option>)}
                  </select>
                </div>
                <div className="text-xs text-[#0A1128]/50 text-right">
                  {c.handled_by && <div>{lang === "fr" ? "Traité par" : "Handled by"} {c.handled_by}</div>}
                  <div>{new Date(c.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US")}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "calls" && (
          <div className="space-y-4" data-testid="calls-list">
            {bookings.length === 0 && <div className="text-[#0A1128]/50 italic">—</div>}
            {bookings.map((b) => (
              <div key={b.booking_id} className="bg-white border border-[#0A1128]/10 p-6 grid md:grid-cols-4 gap-4">
                <div>
                  <div className="font-serif text-lg text-[#0A1128]">{b.name}</div>
                  <div className="text-xs text-[#0A1128]/60">{b.email}</div>
                </div>
                <div className="text-sm">{new Date(b.slot_iso).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}</div>
                <div className="text-sm text-[#0A1128]/70">{b.travel_type} · {b.party_size} pax</div>
                <a href={b.meet_link} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] underline truncate">{b.meet_link}</a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
